import React, { useState, useRef, useEffect } from 'react';
import { Search, Clock, ArrowLeft, Package } from 'lucide-react';
import { OrderTimelineContent } from './OrderTimelineContent';
import { BackButton } from '../../../common/BackButton';

interface OrderTimelineModuleProps {
  onBack?: () => void;
  initialOrderNo?: number;
}

export const OrderTimelineModule: React.FC<OrderTimelineModuleProps> = ({ onBack, initialOrderNo }) => {
  const [searchValue, setSearchValue] = useState(initialOrderNo?.toString() || '');
  const [activeOrderNo, setActiveOrderNo] = useState<number | null>(initialOrderNo || null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!initialOrderNo && inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const parsed = parseInt(searchValue, 10);
    if (!isNaN(parsed) && parsed > 0) {
      setActiveOrderNo(parsed);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          {onBack && <BackButton onClick={onBack} />}
          <Clock className="w-5 h-5 text-blue-600" />
          <h1 className="text-lg font-semibold text-gray-900">Ordretidslinje</h1>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Ordrenummer..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none w-48"
            />
          </div>
          <button
            type="submit"
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Søk
          </button>
        </form>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
        {activeOrderNo ? (
          <OrderTimelineContent orderNo={activeOrderNo} />
        ) : (
          <div className="flex flex-col items-center justify-center py-24 text-gray-400">
            <Package className="w-12 h-12 mb-3 text-gray-300" />
            <p className="text-lg font-medium text-gray-500">Skriv inn et ordrenummer for å se tidslinjen</p>
            <p className="text-sm mt-1">Du kan også åpne tidslinjen fra Ordrehistorikk eller Nye Ordre</p>
          </div>
        )}
      </div>
    </div>
  );
};
